<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { AlignLeft } from "lucide-svelte"
  import PrevNextSkipButtons from "$app/Pages/Plans/Onboarding/components/PrevNextSkipButtons.svelte"
  import Goal from "./components/Goal.svelte"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { onlyTextButtons } from "$app/components/Tiptap/buttons"
  import { onboardingStore } from "./onboarding.svelte"
  import { appStore } from "$app/store.svelte"

  export let root_goal_id: number

  let promise = Promise.resolve()
  let loaded = false
  let errors: any = {}

  onboardingStore.completed = true

  let nameInput: HTMLInputElement

  let long_term_goal: GoalType

  async function getGoals(): Promise<void> {
    try {
      const resp = await xhr.get(
        `/plan_onboarding/${appStore.plan.id}/get_goals.json?goal_type=long_term_goal`
      )

      long_term_goal = resp.data.long_term_goal
      if (long_term_goal) {
        form.id = long_term_goal.id
        form.name = long_term_goal.name
        form.description = long_term_goal.description
        if (form.name?.length > 2) {
          form.name_entered = true
        }
      }
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading your long term goal", "error")
    } finally {
      loaded = true
    }
  }

  promise = getGoals()

  const initialForm = {
    id: null,
    name: "",
    name_entered: false,
    type: "LongTermGoal",
    description: "",
    obstacles_text: "",
    action_plan: "",
    parent_goal_id: root_goal_id,
    parent_goal_type: "RootGoal"
  }

  let form = { ...initialForm }

  async function nextStep(): Promise<void> {
    onboardingStore.saving = true
    errors = {}

    try {
      const resp = await xhr.post(
        `/plan_onboarding/${appStore.$plan.id}/create_goal.json`,
        {
          goal: form,
          plan: { onboarding_step: "next_step" }
        }
      )

      appStore.plan.onboarding_steps = resp.data.plan.onboarding_steps
    } catch (e) {
      console.error(e)
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      onboardingStore.saving = false
    }
  }

  $: {
    if (form.name_entered !== true && nameInput) {
      nameInput.focus()
    }
  }

  function setNameEntered() {
    if (nameInput.value.length > 2) {
      form.name_entered = true
    }
  }

  onMount(() => {
    if (nameInput) {
      nameInput.focus()
    }
  })
</script>

<h2 class="mt-12 text-5xl font-bold text-left text-white">
  Add a Long Term Goal
</h2>

<p class="mt-6 text-lg text-white max-w-2xl">
  Long Term Goals are realistic goals. Generally there are 4 - 6 Long Term Goals
  for a plan. Get started by creating your first one.
</p>

<div class="scale-75">
  <ul class="bg-gray-100 w-full" style="transform: translateX(-100px)">
    <li>
      <Goal goalType="long_term_goal" name={form.name} />
    </li>
  </ul>
</div>

{#if loaded}
  <div class="space-y-8 mt-6 md:mt-8 max-w-lg">
    <div>
      <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4">
        <div>
          <label for="name" class="text-white text-lg">Name </label>
          <div class="mt-2 input-cont" class:error={errors.name}>
            <input
              type="text"
              placeholder="Goal name"
              bind:value={form.name}
              bind:this={nameInput}
              on:blur={setNameEntered}
              class="input input-lg"
            />
            <div class="msg">{errors.name || ""}</div>
          </div>
        </div>

        <div>
          <div class="flex items-center mb-1">
            <AlignLeft class="text-gray-200 mr-2 h-4 w-4" />
            <label class="text-white text-lg" for="description">
              Description
              <span class="text-sm text-gray-300 italic">(Optional)</span>
            </label>
          </div>
          <Tiptap
            content={form.description}
            onupdate={({ html }) => (form.description = html)}
            placeholder="Please add a description of what is going to be accomplished"
            id="long_term_description"
            buttons={onlyTextButtons}
          />
        </div>
      </div>

      <PrevNextSkipButtons nextStepFn={nextStep} />
    </div>
  </div>
{/if}
